import { useState, useEffect, useMemo } from 'react'
import { View, Text, TouchableOpacity, StyleSheet } from 'react-native'
import * as Clipboard from 'expo-clipboard'
import { Share } from 'react-native'
import { useTranslation } from 'react-i18next'
import { supabase } from '../../lib/supabase'
import { showConfirm } from '../../lib/platform'
import { colors, fonts } from '../../lib/theme'
import { useThemeColors } from '../../lib/useThemeColors'
import { useAuth } from '../../lib/AuthContext'
import { Card, SubHeader } from '../../components/Leagues/ui'
import Ionicons from '@expo/vector-icons/Ionicons'
import MaterialCommunityIcons from '@expo/vector-icons/MaterialCommunityIcons'
import GradientButton from '../../components/GradientButton'

export default function SezioneInvito({ leagueId, leagueName, isAdmin }) {
  const { t } = useTranslation()
  const tc = useThemeColors()
  const styles = useMemo(() => makeStyles(tc), [tc])
  const { user } = useAuth()
  const [token, setToken] = useState(null)
  const [isPublic, setIsPublic] = useState(true)
  const [copied, setCopied] = useState(false)
  const [loading, setLoading] = useState(true)
  const [regenerating, setRegen] = useState(false)

  useEffect(() => {
    if (!isAdmin || !leagueId) return
    supabase.from('dim_league').select('invite_token, is_public').eq('id_league', leagueId).single()
      .then(({ data }) => { setToken(data?.invite_token ?? null); setIsPublic(data?.is_public ?? true); setLoading(false) })
  }, [leagueId, isAdmin])

  if (!isAdmin) return null

  const inviteUrl = token
    ? `https://app.virtuacycling.com/join?league=${leagueId}&token=${token}`
    : `https://app.virtuacycling.com/join?league=${leagueId}`

  const handleCopy = async () => {
    await Clipboard.setStringAsync(inviteUrl)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  const handleShare = async () => {
    try {
      await Share.share({
        title: t('league.invite_share_title', { name: leagueName }),
        message: t('league.invite_share_message', { name: leagueName, url: inviteUrl }),
        url: inviteUrl
      })
    } catch { handleCopy() }
  }

  const handleRegen = async () => {
    if (!await showConfirm(t('league.invite_regen_confirm'))) return
    setRegen(true)
    try {
      const { data } = await supabase.rpc('fn_regenerate_invite_token', { p_caller: user.id, p_id_league: leagueId })
      if (data?.[0]?.success) setToken(data[0].new_token)
    } catch { } finally { setRegen(false) }
  }

  return (
    <Card>
      <SubHeader icon={<Ionicons name="link-outline" size={16} color={tc.rosaText} />}>{t('league.invite_section')}</SubHeader>
      <Text style={[styles.settingDesc, { color: tc.textMuted, marginBottom: 12 }]}>
        {t(!isPublic ? 'league.invite_desc_pwd' : 'league.invite_desc')}
      </Text>
      {loading ? (
        <Text style={{ color: tc.textMuted, fontSize: 12, marginBottom: 12 }}>{t('league.invite_loading')}</Text>
      ) : (
        <>
          <View style={[styles.urlBox, { backgroundColor: tc.bgCard2, borderColor: tc.border }]}>
            <Text style={[styles.urlText, { color: tc.textMuted }]} numberOfLines={2}>{inviteUrl}</Text>
          </View>
          <View style={{ flexDirection: 'row', gap: 8, marginBottom: !isPublic ? 10 : 0 }}>
            <TouchableOpacity
              style={[styles.copyBtn, { backgroundColor: copied ? tc.successBg : tc.bgCard2, borderColor: copied ? tc.success : tc.border }]}
              onPress={handleCopy}
              accessibilityRole="button"
              accessibilityLabel={t('league.invite_copy')}
            >
              <Text style={[styles.copyBtnText, { color: copied ? tc.success : tc.textMuted }]}>
                {copied ? t('league.invite_copied') : t('league.invite_copy')}
              </Text>
            </TouchableOpacity>
            <GradientButton onPress={handleShare} size="md" style={{ flex: 1 }}>{t('league.invite_share_btn')}</GradientButton>
          </View>
          {!isPublic && (
            <TouchableOpacity style={[styles.regenBtn, { borderColor: tc.border, opacity: regenerating ? 0.5 : 1 }]} onPress={handleRegen} disabled={regenerating} accessibilityRole="button" accessibilityLabel={t('league.invite_regen_btn')} accessibilityState={{ disabled: regenerating }}>
              <Text style={[styles.regenBtnText, { color: tc.textMuted }]}>
                {regenerating ? t('league.invite_regenerating') : t('league.invite_regen_btn')}
              </Text>
            </TouchableOpacity>
          )}
        </>
      )}
    </Card>
  )
}

const makeStyles = (tc) => StyleSheet.create({
  settingDesc: { fontSize: 12, lineHeight: 18, marginTop: -8 },
  urlBox: { borderRadius: 8, padding: 10, paddingHorizontal: 12, marginBottom: 12, borderWidth: 1 },
  urlText: { fontSize: 11, fontFamily: 'monospace' },
  copyBtn: { flex: 1, borderWidth: 1, borderRadius: 8, padding: 10, minHeight: 44, justifyContent: 'center', alignItems: 'center' },
  copyBtnText: { fontFamily: fonts.condensedBold, fontSize: 13 },
  regenBtn: { width: '100%', borderWidth: 1, borderRadius: 8, padding: 8, minHeight: 44, justifyContent: 'center', alignItems: 'center', marginTop: 8 },
  regenBtnText: { fontFamily: fonts.condensedBold, fontSize: 13 },
})